import type { League, Round } from '@h2h/shared';
import { NationalFlagEmblem } from '../../components/ui/NationalFlagEmblems';
import { ArrowRight, Check, ChevronRight } from '../../components/ui/icons';
import { useJoinLeague, useCurrentRound, useMyEntry, useRoundFixtures } from '../../hooks/useQueries';
import { cn } from '../../lib/cn';
import { fixtureScoreline, isRoundInPlay, liveFixture, picksOpenCount } from './helpers';

export function LiveMoreSection({
  leagues,
  joinedLeagueIds,
  uid,
  onOpen
}: {
  leagues: League[];
  joinedLeagueIds: Set<string>;
  uid: string | null;
  onOpen: (leagueId: string) => void;
}) {
  if (leagues.length === 0) return null;

  return (
    <>
      <div className="flex items-center justify-between px-0 pb-[11px] pt-[22px] md:pt-0">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-extrabold tracking-tight text-ink">More live right now</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-accent-wash px-2 py-[3px]">
            <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent" />
            <span className="num text-[10px] font-extrabold tabular-nums text-accent-ink">{leagues.length}</span>
          </span>
        </div>
        <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-ink2 md:hidden">
          Swipe
          <ChevronRight width={13} height={13} strokeWidth={2.4} className="text-ink2" />
        </span>
      </div>
      <div className="-mx-safe flex gap-[11px] overflow-x-auto px-safe pb-1 [-webkit-overflow-scrolling:touch] md:mx-0 md:flex-col md:gap-2.5 md:overflow-visible md:px-0 md:pb-0">
        {leagues.map((league) => (
          <LiveRailSlot
            key={league.leagueId}
            league={league}
            joined={joinedLeagueIds.has(league.leagueId)}
            uid={uid}
            onOpen={() => onOpen(league.leagueId)}
          />
        ))}
      </div>
    </>
  );
}

function LiveRailSlot({
  league,
  joined,
  uid,
  onOpen
}: {
  league: League;
  joined: boolean;
  uid: string | null;
  onOpen: () => void;
}) {
  const { data: round } = useCurrentRound(league.leagueId, league.currentRoundId);
  if (!isRoundInPlay(round?.status)) return null;
  return <LiveRailCard league={league} joined={joined} uid={uid} onOpen={onOpen} round={round} />;
}

function LiveRailCard({
  league,
  joined,
  uid,
  onOpen,
  round
}: {
  league: League;
  joined: boolean;
  uid: string | null;
  onOpen: () => void;
  round: Round | null | undefined;
}) {
  const join = useJoinLeague(uid);
  const { data: entry } = useMyEntry(league.leagueId, league.currentRoundId, uid);
  const { data: fixtures } = useRoundFixtures(league.leagueId, league.currentRoundId);

  const open = round?.status === 'OPEN' || round?.status === 'LOCKING';
  const needsPicks = open && !entry;
  const picked = entry?.duelPicks?.length ?? entry?.predictions?.length ?? 0;
  const openPicks = picksOpenCount(round, picked);
  const live = liveFixture(fixtures);
  // This rail is cycling-only, so a still-open stage means the duels are open to
  // call — not that the race is rolling. Say "duels" and only badge it LIVE once
  // the stage is actually under way (LOCKING/SETTLING), so a stage that departs
  // tomorrow doesn't read as live tonight.
  const footer = needsPicks
    ? openPicks === 1
      ? '1 duel open'
      : `${openPicks} duels open`
    : entry && open
      ? 'Locked in'
      : 'Play now';

  const stageUnderWay = round?.status === 'LOCKING' || round?.status === 'SETTLING';
  const badgeLabel = live?.elapsed != null ? `${live.elapsed}'` : stageUnderWay ? 'LIVE' : 'OPEN';

  const handle = () => {
    if (!joined) {
      if (!join.isPending) join.mutate(league.leagueId, { onSuccess: onOpen });
      return;
    }
    onOpen();
  };

  return (
    <button
      type="button"
      onClick={handle}
      disabled={join.isPending}
      className="tap w-[178px] shrink-0 rounded-[20px] bg-[radial-gradient(120%_100%_at_85%_-10%,#19311F_0%,#10131A_55%,#07070B_100%)] p-[14px] text-left text-white shadow-[0_14px_28px_-20px_rgba(0,0,0,0.6)] disabled:opacity-80 md:w-full"
    >
      <div className="flex items-center justify-between">
        <NationalFlagEmblem
          leagueId={league.leagueId}
          country={league.country}
          slug={league.cycling?.slug}
          size="xs"
        />
        <span className="inline-flex items-center gap-[5px] rounded-full border border-accent/40 bg-accent/[0.16] px-2 py-[3px]">
          <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent" />
          <span className="num text-[9.5px] font-extrabold tabular-nums text-accent">{badgeLabel}</span>
        </span>
      </div>
      <div className="mt-[13px] text-[13px] font-extrabold tracking-snug text-white">{league.name}</div>
      <div className="mt-0.5 truncate text-[11px] font-medium tabular-nums text-white/60">
        {live ? fixtureScoreline(live) : (round?.label ?? league.cycling?.group ?? 'Cycling')}
      </div>
      <div
        className={cn(
          'mt-[13px] flex items-center gap-[5px] rounded-[12px] border px-[11px] py-2',
          needsPicks ? 'border-accent/40 bg-accent/[0.16]' : 'border-white/10 bg-white/[0.08]'
        )}
      >
        <span className="text-[11px] font-extrabold text-accent">{join.isPending ? 'Joining…' : footer}</span>
        {needsPicks || !entry ? (
          <ArrowRight width={12} height={12} strokeWidth={2.8} className="ml-auto text-accent" />
        ) : (
          <Check width={12} height={12} strokeWidth={2.8} className="ml-auto text-accent" />
        )}
      </div>
    </button>
  );
}
